import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Loader2, GripVertical, Trash2, Plus, Save, Layers } from 'lucide-react';
import { invalidateJobStatusCache } from '@/hooks/useJobStatusConfig';
import { invalidateFormFlowsCache } from '@/hooks/useFormStatusFlows';

const WORK_TYPES = [
  { key: 'well_construction', label: 'Well Construction' },
  { key: 'pump_installer', label: 'Pump Installer' },
  { key: 'filters_service', label: 'Filters & Service' },
  { key: 'site_inspection', label: 'Site Inspection' },
  { key: 'office', label: 'Office (Job)' },
  { key: 'office_workflow', label: 'Office Workflow' },
  { key: 'office_form', label: 'Office Forms' },
];

/**
 * JobStatusSettings — Settings > Job Status (Alian: "que fuera un job status...
 * para Well Construction, Pump Installer, Filters Service y Office, y yo
 * poder poner los status y administrarlos yo mismo"). Add/rename/reorder
 * (drag & drop)/delete statuses per work type. The Job Detail status bar and
 * the Jobs list filters read straight from this config.
 */
export default function JobStatusSettings() {
  const { t } = useTranslation();
  const [activeType, setActiveType] = useState('well_construction');
  const [lists, setLists] = useState({});
  const [formFlows, setFormFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [draggedIdx, setDraggedIdx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [res, flowsRes] = await Promise.all([
        api.get('/job-status'),
        api.get('/job-status/form-flows').catch(() => ({ data: [] })),
      ]);
      const flows = flowsRes.data || [];
      setFormFlows(flows);
      const merged = { ...(res.data || {}) };
      flows.forEach(f => { merged[`form:${f.form_id}`] = f.statuses; });
      setLists(merged);
    } catch {
      toast.error(t('errors.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const current = lists[activeType] || [];

  const updateCurrent = (next) => setLists(prev => ({ ...prev, [activeType]: next }));

  const handleAdd = () => {
    if (!newLabel.trim()) return;
    const key = newLabel.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (!key) return;
    if (current.some(s => s.key === key)) {
      toast.error('Ya existe un status con esa clave');
      return;
    }
    updateCurrent([...current, { key, label: newLabel.trim(), is_final: false }]);
    setNewLabel('');
  };

  const handleRename = (idx, label) => {
    updateCurrent(current.map((s, i) => (i === idx ? { ...s, label } : s)));
  };

  const handleToggleFinal = (idx, val) => {
    updateCurrent(current.map((s, i) => (i === idx ? { ...s, is_final: val } : s)));
  };

  const handleDelete = (idx) => {
    if (current.length <= 1) {
      toast.error('Debe quedar al menos un status');
      return;
    }
    updateCurrent(current.filter((_, i) => i !== idx));
  };

  const handleDragStart = (idx) => setDraggedIdx(idx);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (idx) => {
    if (draggedIdx === null || draggedIdx === idx) return;
    const next = [...current];
    const [moved] = next.splice(draggedIdx, 1);
    next.splice(idx, 0, moved);
    updateCurrent(next);
    setDraggedIdx(null);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const url = activeType.startsWith('form:')
        ? `/job-status/form/${activeType.slice(5)}`
        : `/job-status/${activeType}`;
      const res = await api.put(url, { statuses: current });
      updateCurrent(res.data);
      invalidateJobStatusCache();
      invalidateFormFlowsCache();
      toast.success(t('success.saved'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errors.savingError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }

  return (
    <Card className="bg-white border-gray-100" data-testid="job-status-settings">
      <CardContent className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-2"><Layers className="w-4 h-4 text-[#3399FF]" /> Job Status</p>
            <p className="text-xs text-gray-400 mt-0.5">Agregue, renombre, reordene (arrastrar) o borre los estados de cada tipo de trabajo. Marque "Final" para que quede al fondo de la lista.</p>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-2">
          {WORK_TYPES.map(wt => (
            <button
              key={wt.key}
              type="button"
              onClick={() => setActiveType(wt.key)}
              data-testid={`job-status-worktype-${wt.key}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeType === wt.key ? 'bg-[#003366] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >
              {wt.label}
            </button>
          ))}
        </div>

        {/* Un flujo POR FORMA (Excel "Office Work Flow (2)"): Permit
            Application, Completion Report, NOC, NTO, y cualquier forma nueva. */}
        {formFlows.length > 0 && (
          <div className="mb-4" data-testid="job-status-form-flows">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold mb-1.5">Estados por forma</p>
            <div className="flex flex-wrap gap-1.5">
              {formFlows.map(f => (
                <button
                  key={f.form_id}
                  type="button"
                  onClick={() => setActiveType(`form:${f.form_id}`)}
                  data-testid={`job-status-form-${f.form_id}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeType === `form:${f.form_id}` ? 'bg-[#3399FF] text-white' : 'bg-blue-50 text-[#003366] hover:bg-blue-100'}`}
                >
                  {f.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg" data-testid="job-status-list">
          {current.map((s, idx) => (
            <div
              key={s.key}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={handleDragOver}
              onDrop={() => handleDrop(idx)}
              className={`flex items-center gap-2 px-3 py-2 ${draggedIdx === idx ? 'opacity-40' : ''}`}
              data-testid={`job-status-row-${s.key}`}
            >
              <GripVertical className="w-4 h-4 text-gray-300 cursor-grab flex-shrink-0" />
              <Input
                value={s.label}
                onChange={e => handleRename(idx, e.target.value)}
                className="h-8 text-sm flex-1"
                data-testid={`job-status-label-${s.key}`}
              />
              <span className="text-[10px] text-gray-400 font-mono flex-shrink-0 w-24 truncate" title={s.key}>{s.key}</span>
              <label className="flex items-center gap-1.5 text-xs text-gray-500 flex-shrink-0">
                <Switch checked={!!s.is_final} onCheckedChange={v => handleToggleFinal(idx, v)} data-testid={`job-status-final-${s.key}`} />
                Final
              </label>
              <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 flex-shrink-0" onClick={() => handleDelete(idx)} data-testid={`job-status-delete-${s.key}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 mt-3">
          <Input
            value={newLabel}
            onChange={e => setNewLabel(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="Nuevo status (ej. Waiting for Materials)"
            className="h-9 text-sm flex-1"
            data-testid="job-status-new-input"
          />
          <Button variant="outline" onClick={handleAdd} data-testid="job-status-add-btn">
            <Plus className="w-4 h-4 mr-1.5" /> Agregar
          </Button>
        </div>

        <div className="flex justify-end mt-4">
          <Button className="bg-[#003366] hover:bg-[#004080] text-white" onClick={handleSave} disabled={saving} data-testid="job-status-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />} Guardar cambios
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
